import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { TabbedFlipboardMediaCardType } from '@models/ITabbedFlipboard';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import classNames from 'classnames';

interface Props extends Omit<TabbedFlipboardMediaCardType, 'type'> {
  readonly dataTestId: string;
  readonly isSmall?: boolean;
}

const TabbedFlipboardMediaCard = ({
  headline,
  media,
  dataTestId,
  meta,
  isSmall,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const isPreview = usePreviewMode();

  return (
    <div
      className={classNames('relative h-full w-full', {
        'max-h-[540px] max-w-[540px]': isPreview,
      })}
      data-testid={dataTestId}
    >
      <p
        className={classNames(
          'absolute top-4 left-4 z-10 text-white max-lg:right-[42px] lg:top-6',
          isSmall || isMobile ? 'h5' : 'h3',
          isSmall ? 'lg:left-7 lg:w-1/2' : 'lg:right-8 lg:left-8'
        )}
        data-testid={`${dataTestId}-Headline`}
        data-epi-property-name={meta?.editHints?.headline}
      >
        {headline}
      </p>

      <div
        className="absolute top-0 z-[1] h-full w-full rounded-lg bg-gradient-to-b from-black-100/40"
        data-testid={`${dataTestId}-Gradient`}
      />

      {isPreview && (
        <div
          className="absolute bottom-0 h-1/2 w-full"
          data-epi-property-name={meta?.editHints?.media}
        />
      )}

      {media &&
        ('url' in media && media.url ? (
          <InViewVideo
            videoClasses="[&>div>video]:object-cover [&>div>video]:rounded-lg h-full"
            wrapperClasses="h-full"
            autoPlay
            hasInVideoPlayButton
            inVideoPlayButtonPositionClassName="bottom-2.5 left-2.5 md:bottom-5 md:left-5"
            controls={false}
            video={media}
            dataTestId={`${dataTestId}-Video`}
            playOnCoverClick={false}
          />
        ) : (
          <Image
            image={media as IImage}
            imageClasses="w-full object-cover h-full rounded-lg"
            dataTestId={`${dataTestId}-Image`}
          />
        ))}
    </div>
  );
};

export default TabbedFlipboardMediaCard;
